/**
 * The verdict column of a brief (DESIGN.md "Layout", "Verdict first"): the
 * action-first h1 (or h2, `headingLevel`) in Plex Light, one lede naming
 * who or what, the actions row (the view's one Soft Green primary action,
 * then the caller's tertiary, then any view `controls`) and the provenance
 * line led by the iuvo mark. The primary is the brief's route link, or a
 * button (`primaryAction`) for a mutation or a download.
 *
 * `brief` takes any composer's output: it goes through `toActionBrief()`, so
 * pages never read brief fields themselves (blueprint §4.7). The emphasized
 * subject is a 400 span (not an <em>, which browsers italicize) and takes a
 * pillar's ink only when it IS that pillar's name (the Pillar-Name Ink Rule).
 *
 * `size="tile"` is the brief inside a tile (the license-waste tile): a 1.25rem/400 headline (usually an h3 under the
 * tile's own h2) and a body lede, with no outer padding.
 *
 * The score tile beside it is the caller's: this renders the column only.
 */
import clsx from 'clsx'
import { toActionBrief, type ActionBrief, type BriefPillar } from '@/lib/brief-shape'
import { Button, ButtonLink } from './Button'
import { IconArrowRight } from './icons'
import { IuvoMark } from './IuvoMark'
import { Marked } from './Marked'
import type { BriefHeroProps } from './types'

const INK: Record<BriefPillar, string> = {
  compliance: 'text-cat-baseline-ink',
  bpa: 'text-cat-bpa-ink',
  identity: 'text-cat-identity-ink',
}

function Headline({ brief }: { brief: ActionBrief }) {
  const { headline, emphasis } = brief
  const at = emphasis ? headline.indexOf(emphasis.text) : -1
  if (!emphasis || at < 0) return <>{headline}</>
  return (
    <>
      {headline.slice(0, at)}
      <span className={clsx('font-normal', emphasis.pillar ? INK[emphasis.pillar] : 'text-text-primary')}>
        {emphasis.text}
      </span>
      {headline.slice(at + emphasis.text.length)}
    </>
  )
}

export function BriefHero({
  brief: input,
  primaryAction: buttonAction,
  secondaryAction,
  controls,
  provenance,
  size = 'hero',
  headingId = 'brief-verdict',
  headingLevel: Heading = 'h1',
  isLoading = false,
  className,
}: BriefHeroProps) {
  const brief = toActionBrief(input)
  const empty = isLoading || !brief.headline
  // A button primary replaces the brief's link primary: one primary per view.
  const linkAction = buttonAction ? undefined : brief.primaryAction
  const showActions = !empty && !!(buttonAction || linkAction || secondaryAction)
  const hasActions = showActions || !!controls
  const tile = size === 'tile'

  return (
    <div className={clsx('min-w-0', !tile && 'py-2', className)}>
      <Heading
        id={headingId}
        aria-busy={empty || undefined}
        className={clsx(
          tile
            ? 'm-0 text-balance font-normal tracking-normal text-text-primary max-w-[40ch] text-heading'
            : 'm-0 text-balance font-light tracking-normal text-text-primary max-c-md:text-[2.25rem]',
          !tile && (size === 'hero' ? 'max-w-[18ch] text-display' : 'max-w-[22ch] text-headline'),
        )}
      >
        {empty ? (
          <>
            <span aria-hidden="true" className="block h-[1.17em] w-[12ch] max-w-full bg-layer-hover" />
            <span className="sr-only">Loading the brief</span>
          </>
        ) : (
          <Headline brief={brief} />
        )}
      </Heading>
      {!empty && brief.lede ? (
        <p
          className={
            tile
              ? 'm-0 mt-2 max-w-[56ch] text-body text-text-secondary'
              : 'm-0 mt-6 max-w-[56ch] text-heading font-light text-text-secondary max-c-md:text-[1.125rem]'
          }
        >
          <Marked text={brief.lede} />
        </p>
      ) : null}
      {hasActions ? (
        <div className={clsx(tile ? 'mt-6' : 'mt-8', 'flex flex-wrap gap-4 max-c-md:[&>*]:w-full max-c-md:[&>*]:max-w-none')}>
          {showActions && buttonAction ? (
            <Button
              kind="primary"
              onClick={buttonAction.onClick}
              loading={buttonAction.loading}
              disabled={buttonAction.disabled}
              aria-label={buttonAction['aria-label']}
              icon={buttonAction.icon ?? <IconArrowRight />}
            >
              {buttonAction.label}
            </Button>
          ) : null}
          {showActions && linkAction ? (
            <ButtonLink to={linkAction.href} kind="primary" icon={<IconArrowRight />}>
              {linkAction.label}
            </ButtonLink>
          ) : null}
          {showActions ? secondaryAction : null}
          {controls}
        </div>
      ) : null}
      {provenance ? (
        <p className="m-0 mt-5 flex items-center gap-2 text-helper text-text-helper">
          <IuvoMark size={14} />
          <span className="min-w-0">{provenance}</span>
        </p>
      ) : null}
    </div>
  )
}

export default BriefHero
